/**
 * Display helpers for feed, signal and alert screens. Times are shown in UTC, because
 * provider schedules and late thresholds are defined in UTC (DATA.md §8).
 */

const DATE_TIME = new Intl.DateTimeFormat('en-GB', {
  timeZone: 'UTC',
  day: 'numeric',
  month: 'short',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
})

const DATE = new Intl.DateTimeFormat('en-GB', {
  timeZone: 'UTC',
  day: 'numeric',
  month: 'short',
  year: 'numeric',
})

const SHORT_DAY = new Intl.DateTimeFormat('en-GB', {
  timeZone: 'UTC',
  weekday: 'short',
  day: 'numeric',
  month: 'short',
})

function parse(value: string | null | undefined): Date | null {
  if (!value) return null
  const date = new Date(/^\d{4}-\d{2}-\d{2}$/.test(value) ? `${value}T00:00:00Z` : value)
  return Number.isNaN(date.getTime()) ? null : date
}

/** "22 Oct 2026, 09:15 UTC"; `fallback` when missing. */
export function formatDateTime(value: string | null | undefined, fallback = '—'): string {
  const date = parse(value)
  return date ? `${DATE_TIME.format(date)} UTC` : fallback
}

/** "22 Oct 2026". Missing dates read "unverified" by default (GAP-TP-016, R-16). */
export function formatDate(value: string | null | undefined, fallback = 'unverified'): string {
  const date = parse(value)
  return date ? DATE.format(date) : fallback
}

/** "Thu 22 Oct" for day buckets. */
export function formatDay(value: string): string {
  const date = parse(value)
  return date ? SHORT_DAY.format(date) : value
}

/** "3 min ago", "in 45 min", "2 h ago", "5 days ago". */
export function formatRelative(
  value: string | null | undefined,
  now: number = Date.now(),
): string | null {
  const date = parse(value)
  if (!date) return null
  const diff = date.getTime() - now
  const abs = Math.abs(diff)
  const minutes = Math.round(abs / 60_000)
  let text: string
  if (minutes < 1) text = 'less than a minute'
  else if (minutes < 60) text = `${minutes} min`
  else if (minutes < 48 * 60) text = `${Math.round(minutes / 60)} h`
  else text = `${Math.round(minutes / 1440)} days`
  if (minutes < 1) return diff >= 0 ? 'in under a minute' : 'just now'
  return diff >= 0 ? `in ${text}` : `${text} ago`
}

/** "1 min 12 s", "850 ms". */
export function formatDuration(ms: number | null | undefined): string {
  if (ms === null || ms === undefined) return '—'
  if (ms < 1000) return `${ms} ms`
  const seconds = Math.round(ms / 1000)
  if (seconds < 60) return `${seconds} s`
  const minutes = Math.floor(seconds / 60)
  const rest = seconds % 60
  return rest ? `${minutes} min ${rest} s` : `${minutes} min`
}

/** 0.957 → "96%"; null → "—". */
export function formatPercent(value: number | null | undefined): string {
  if (value === null || value === undefined) return '—'
  return `${Math.round(value * 100)}%`
}

/** A count that is null for failed runs (GAP-TP-012): never shown as zero. */
export function formatCount(value: number | null | undefined): string {
  return value === null || value === undefined ? '—' : value.toLocaleString('en-GB')
}

export function plural(count: number, one: string, many = `${one}s`): string {
  return `${count.toLocaleString('en-GB')} ${count === 1 ? one : many}`
}
